/*
 *
 * DashboardSecuritySettings action/reducer
 *
 */

import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { push } from "connected-react-router";
import {
  setToast,
  getTnCForCidpUser,
  setUserAcceptedNewTnC,
} from "scenes/redux";
import { createActionDispatchers } from "utils/reduxHelpers";
import { Authenticators, Credential } from "./types";

export type State = {
  loading: boolean;
  authenticators: Array<Authenticators>;
  passkeys: Array<Credential>;
};

export const initialState: State = {
  loading: false,
  authenticators: [],
  passkeys: [],
};

export const deletePasskey = createAction<{ credentialId: string }>(
  "dashboardSecuritySettings/deletePasskey"
);

const slice = createSlice({
  name: "dashboardSecuritySettings",
  initialState,
  // create actions with reducer (slice action) here
  reducers: {
    loadAuthenticators(state) {
      state.loading = true;
    },
    fetchPasskeys(state) {
      state.loading = true;
    },
    afterAuthenticatorsLoaded(
      state: State,
      action: PayloadAction<{ authenticators: Array<Authenticators> }>
    ) {
      state.loading = false;
      state.authenticators = (action.payload.authenticators || []).filter(
        (authenticator) => authenticator.type !== "passkey"
      );
    },
    afterPasskeysLoaded(
      state: State,
      action: PayloadAction<{
        credentials: Array<Credential>;
      }>
    ) {
      state.loading = false;
      if (action.payload.credentials) {
        state.passkeys =
          action.payload.credentials.filter(
            (credential) => credential.type === "PASSKEY"
          ) ?? [];
      } else {
        state.passkeys = [];
      }
    },
    afterPasskeyDeleted(
      state: State,
      action: PayloadAction<{ credentialId: string }>
    ) {
      state.passkeys = state.passkeys.filter(
        (passkey) => passkey.credentialId !== action.payload.credentialId
      );
    },
  },
  extraReducers: (builder) => {},
});

/**
 * actions from slice to be exported for saga
 */
export const {
  loadAuthenticators,
  afterAuthenticatorsLoaded,
  fetchPasskeys,
  afterPasskeysLoaded,
  afterPasskeyDeleted,
} = slice.actions;

/**
 * all actions for layout
 */
export const actionProps = createActionDispatchers(
  slice.actions,
  // slice actions
  ["loadAuthenticators", "fetchPasskeys"],
  // pure actions
  {
    setToast,
    getTnCForCidpUser,
    setUserAcceptedNewTnC,
    deletePasskey,
    goToDashboard: () => push("/dashboard/home"),
    goToDashboardMfa: () => push("/dashboard/Mfa"),
    goToEditUsername: () => push("/dashboard/securitysettings/edit-username"),
    goToEditPassword: () => push("/dashboard/securitysettings/edit-password"),
  }
);

export default slice.reducer;
